import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixOffIcon from '@mui/icons-material/AutoFixOff';

const COLORS = ['#111111', '#1565C0', '#D32F2F', '#2E7D32', '#7B1FA2', '#F07828'];

export default function Whiteboard({
  canDraw,
  allowEraser = true,
  onStroke,
  onText,
  onClear,
  onRequestDraw,
  onStopShare,
  remoteStroke,
  remoteText,
  clearRevision = 0,
}) {
  const canvasRef = useRef(null);
  const boardWrapRef = useRef(null);
  const drawingRef = useRef([]);
  const historyRef = useRef([]);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#111111');
  const [typedText, setTypedText] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => { if (!allowEraser && tool === 'eraser') setTool('pen'); }, [allowEraser, tool]);

  const drawStroke = (stroke) => {
    const canvas = canvasRef.current;
    if (!canvas || !stroke?.points || stroke.points.length < 2) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Number(stroke.width) || 3;
    ctx.globalCompositeOperation = stroke.tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.strokeStyle = stroke.color || '#111111';
    ctx.beginPath();

    stroke.points.forEach((pt, i) => {
      const x = Number(pt.x) * rect.width;
      const y = Number(pt.y) * rect.height;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });

    ctx.stroke();
    ctx.restore();
  };

  const drawText = (item) => {
    const canvas = canvasRef.current;
    if (!canvas || !item?.text) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    ctx.save();
    ctx.fillStyle = item.color || '#111111';
    ctx.font = `${Math.max(12, Number(item.fontSize) || 20)}px sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillText(String(item.text), Number(item.x) * rect.width, Number(item.y) * rect.height);
    ctx.restore();
  };

  const replay = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    historyRef.current.forEach((item) => item.kind === 'text' ? drawText(item) : drawStroke(item));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const fit = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
      replay();
    };

    // Let the browser finish applying :fullscreen dimensions first.
    const raf = window.requestAnimationFrame(fit);
    window.addEventListener('resize', fit);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', fit);
    };
  }, [isFullscreen]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === boardWrapRef.current);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!remoteStroke) return;
    historyRef.current.push({ ...remoteStroke, kind: 'stroke' });
    drawStroke(remoteStroke);
  }, [remoteStroke]);

  useEffect(() => {
    if (!remoteText) return;
    historyRef.current.push({ ...remoteText, kind: 'text' });
    drawText(remoteText);
  }, [remoteText]);

  useEffect(() => {
    historyRef.current = [];
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  }, [clearRevision]);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const down = (e) => {
    if (!canDraw) return;

    if (tool === 'type') {
      const text = typedText.trim();
      if (!text) return;

      const p = point(e);
      const item = {
        id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        text: text.slice(0, 500),
        x: p.x,
        y: p.y,
        fontSize: 20,
        color,
      };

      historyRef.current.push({ ...item, kind: 'text' });
      drawText(item);
      onText?.(item);
      setTypedText('');
      return;
    }

    canvasRef.current.setPointerCapture(e.pointerId);
    drawingRef.current = [point(e)];
  };

  const move = (e) => {
    if (!canDraw || tool === 'type' || !drawingRef.current.length) return;

    const next = point(e);
    const prev = drawingRef.current[drawingRef.current.length - 1];
    drawingRef.current.push(next);

    drawStroke({
      points: [prev, next],
      width: tool === 'eraser' ? 18 : 3,
      tool,
      color,
    });
  };

  const up = () => {
    if (!canDraw || tool === 'type' || drawingRef.current.length < 2) {
      drawingRef.current = [];
      return;
    }

    const stroke = {
      id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      points: [...drawingRef.current],
      width: tool === 'eraser' ? 18 : 3,
      tool,
      color,
    };

    historyRef.current.push({ ...stroke, kind: 'stroke' });
    onStroke?.(stroke);
    drawingRef.current = [];
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await boardWrapRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (e) {
      console.error('Whiteboard fullscreen:', e);
    }
  };

  return (
    <Box ref={boardWrapRef} sx={{
      bgcolor: '#fff',
      p: 1,
      borderRadius: 2,
      boxSizing: 'border-box',
      ...(isFullscreen ? {
        width: '100vw',
        height: '100vh',
        minHeight: '100vh',
        p: 1.5,
        borderRadius: 0,
        bgcolor: '#F2F2F2',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      } : {}),
      '&:fullscreen': {
        width: '100vw',
        height: '100vh',
        minHeight: '100vh',
        overflow: 'hidden',
        p: 1.5,
        borderRadius: 0,
        bgcolor: '#F2F2F2',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      },
    }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={.75} flexWrap="wrap">
          <Tooltip title="Type"><span><IconButton size="small" disabled={!canDraw} color={tool === 'type' ? 'primary' : 'default'} onClick={() => setTool('type')}><TextFieldsIcon /></IconButton></span></Tooltip>
          <Tooltip title="Pen"><span><IconButton size="small" disabled={!canDraw} color={tool === 'pen' ? 'primary' : 'default'} onClick={() => setTool('pen')}><EditIcon /></IconButton></span></Tooltip>
          {allowEraser && <Tooltip title="Eraser"><span><IconButton size="small" disabled={!canDraw} color={tool === 'eraser' ? 'primary' : 'default'} onClick={() => setTool('eraser')}><AutoFixOffIcon /></IconButton></span></Tooltip>}
          {onClear && <Button size="small" color="error" variant="outlined" onClick={onClear}>Clear board</Button>}
          <Button size="small" variant="outlined" onClick={toggleFullscreen}>{isFullscreen ? 'Exit full screen' : 'Full screen'}</Button>
        </Stack>

        {tool !== 'eraser' && canDraw && (
          <Stack direction="row" spacing={.5} alignItems="center">
            {COLORS.map((c) => (
              <Tooltip key={c} title={c}>
                <Box onClick={() => setColor(c)} sx={{
                  width: 24, height: 24, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                  border: color === c ? '3px solid #fff' : '1px solid rgba(0,0,0,.25)',
                  boxShadow: color === c ? '0 0 0 2px #1565C0' : 'none',
                }} />
              </Tooltip>
            ))}
            <Box component="input" type="color" value={color}
              onChange={(e) => setColor(e.target.value)}
              sx={{ width: 32, height: 28, border: 0, p: 0 }} />
          </Stack>
        )}

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          sx={{ ml: { md: 'auto' } }}
        >
          <Chip
            size="small"
            label={canDraw ? 'Mouse, touch or stylus supported' : 'View only'}
            color={canDraw ? 'success' : 'default'}
          />

          {onRequestDraw && (
            <Button
              size="small"
              variant="contained"
              onClick={onRequestDraw}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Ask permission to use whiteboard
            </Button>
          )}

          {onStopShare && (
            <Button
              size="small"
              variant="outlined"
              onClick={onStopShare}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Stop sharing
            </Button>
          )}
        </Stack>
      </Stack>

      {tool === 'type' && canDraw && (
        <TextField fullWidth size="small" value={typedText}
          label="Text to place on whiteboard"
          placeholder="Type here, then click on the whiteboard to place it"
          onChange={(e) => setTypedText(e.target.value)}
          helperText="Type first, then click the exact location on the board."
          sx={{ mb: 1 }} />
      )}

      <Box component="canvas" ref={canvasRef}
        onPointerDown={down} onPointerMove={move}
        onPointerUp={up} onPointerCancel={up}
        sx={{
          width: '100%',
          ...(isFullscreen
            ? {
                flex: 1,
                height: 'auto',
                minHeight: 0,
              }
            : {
                height: { xs: 420, md: 620 },
                minHeight: '60vh',
              }),
          bgcolor: '#fff',
          border: '1px solid rgba(25,118,210,.22)',
          borderRadius: 2,
          touchAction: 'none',
          display: 'block',
          cursor: tool === 'type' ? 'text' : canDraw ? 'crosshair' : 'default',
        }}
      />
    </Box>
  );
}
